import {
  useOrganizationsCustomerFindOneQuery,
  useOrganizationsCustomerUpdate,
} from "@/lib/api/requests/organizations.requests";
import { Shield, Building2 } from "lucide-react";
import { useEffect, useState } from "react";

import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { Switch } from "@/ui-components/ui/switch";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { cn } from "@/lib/utils/cn";

const OrganizationAdvancedFeaturesSkeleton = () => {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-4 py-4 sm:px-6 sm:py-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-11 w-11 rounded-2xl sm:h-12 sm:w-12" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-48 sm:h-6 sm:w-56" />
              <Skeleton className="h-4 w-64 sm:w-80" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 sm:p-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <Skeleton className="h-12 w-12 rounded-2xl" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-20 rounded-full" />
                  <Skeleton className="h-4 w-full max-w-md" />
                  <Skeleton className="h-4 w-3/4 max-w-sm" />
                </div>
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const OrganizationAdvancedFeatures = () => {
  const { data: team, isLoading } = useOrganizationsCustomerFindOneQuery();
  const navigate = useNavigate();
  const { mutateAsync: updateOrganization, isPending } =
    useOrganizationsCustomerUpdate();

  const { isAdmin } = usePermissions();

  const enableMandantFeature = team?.enableMandantFeature ?? false;

  const [localMandantEnabled, setLocalMandantEnabled] = useState(
    enableMandantFeature,
  );

  useEffect(() => {
    setLocalMandantEnabled(enableMandantFeature);
  }, [enableMandantFeature]);

  const isMandantActive = team ? localMandantEnabled : false;

  const handleToggleMandantFeature = async () => {
    if (!team || isPending || !isAdmin) return;

    const next = !isMandantActive;
    const previous = isMandantActive;

    setLocalMandantEnabled(next);

    try {
      await updateOrganization({ enableMandantFeature: next });
      toast.success("Mandanten-Feature wurde aktualisiert.");
    } catch (error) {
      setLocalMandantEnabled(previous);
      toast.error("Mandanten-Feature konnte nicht gespeichert werden", { error });
    }
  };

  if (isLoading) {
    return (
      <SubPageLayout
        pageTitle="Erweiterte Funktionen"
        parentPageTitle="Dein Verein"
        parentPageCallback={() => navigate("/vereine")}
        navigateBackCallback={() => navigate("/vereine")}
        icon={<Shield className="h-6 w-6" />}
      >
        <OrganizationAdvancedFeaturesSkeleton />
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      pageTitle="Erweiterte Funktionen"
      parentPageTitle="Dein Verein"
      parentPageCallback={() => navigate("/vereine")}
      navigateBackCallback={() => navigate("/vereine")}
      icon={<Shield className="h-6 w-6" />}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
        <Card className="overflow-hidden border-gray-200 shadow-sm transition-all">
          <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-4 py-4 sm:px-6 sm:py-6">
            <SectionHeader
              icon={Shield}
              title="Erweiterte Funktionen"
              description="Verwalte zusätzliche Funktionen und schalte optionale Vereins-Features gezielt frei."
            />
          </CardHeader>

          <CardContent className="p-4 sm:p-6">
            <div className="grid gap-4">
              <div
                className={cn(
                  "rounded-2xl border bg-white p-4 shadow-sm transition-all sm:p-5",
                  isMandantActive
                    ? "border-emerald-200 bg-emerald-50/30"
                    : "border-gray-200",
                )}
              >
                <div className="flex h-full flex-col gap-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border transition-colors",
                          isMandantActive
                            ? "border-emerald-100 bg-emerald-50 text-emerald-600"
                            : "border-gray-100 bg-gray-50 text-gray-400",
                        )}
                      >
                        <Building2 className="h-6 w-6" />
                      </div>

                      <div className="space-y-2">
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                            Mandanten-Feature
                          </h3>
                          <span
                            className={cn(
                              "inline-flex min-h-6 items-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none",
                              isMandantActive
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-gray-100 text-gray-600",
                            )}
                          >
                            {isMandantActive ? "Aktiv" : "Inaktiv"}
                          </span>
                        </div>

                        <p className="max-w-2xl text-sm leading-relaxed text-gray-500">
                          Weise Einnahmen verschiedenen Mandanten zu, zum Beispiel
                          dem Verein, einer Spielbetriebs GmbH oder einem
                          Foerderverein.
                        </p>
                      </div>
                    </div>

                    {team && isAdmin && (
                      <Switch
                        checked={isMandantActive}
                        onCheckedChange={() => void handleToggleMandantFeature()}
                        disabled={isPending || !isAdmin}
                        className="self-start sm:self-center"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </SubPageLayout>
  );
};
